/* Dialog — generic in-memory modal builder.
   Used for edit/delete/add dialogs whose HTML bodies are fetched from the
   server per-click (so we never pre-render per-row modals).
*/
class Dialog {
    constructor(title, content, options = {}) {
        this.title = title;
        this.content = content;
        this.options = options;
        this.events = [];
        this.buttons = [];
        this.modal = null;
        this.backdrop = null;
    }

    setEvents(events) { this.events = events; return this; }
    setButtons(buttons) { this.buttons = buttons; return this; }

    show() {
        const backdrop = document.createElement('div');
        // z-index nudges up with each stacked dialog so a child modal lands
        // visually above its parent.
        const stackDepth = document.querySelectorAll('.dialog-backdrop').length;
        backdrop.className = 'dialog-backdrop fixed inset-0 flex items-center justify-center bg-slate-900/40 p-4';
        backdrop.style.zIndex = String(50 + stackDepth * 10);
        this.backdrop = backdrop;

        const sizeClass = {
            sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg',
            xl: 'max-w-xl', '2xl': 'max-w-2xl', '3xl': 'max-w-3xl',
        }[this.options.size] || 'max-w-md';

        this.modal = document.createElement('div');
        this.modal.className =
            `w-full ${sizeClass} max-h-[90vh] overflow-hidden flex flex-col ` +
            'rounded-xl bg-white shadow-xl dark:bg-slate-900 dark:shadow-black/40';

        const header = document.createElement('div');
        header.className =
            'flex items-center justify-between border-b border-slate-200 px-5 py-3 dark:border-slate-800';
        header.innerHTML = `
            <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">${this.title}</h3>
            <button type="button" class="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300">
              <i class="bi bi-x-lg"></i>
            </button>
        `;
        header.querySelector('button').addEventListener('click', () => this.hide());

        const body = document.createElement('div');
        body.className = 'px-5 py-4 overflow-y-auto flex-grow text-sm';
        body.innerHTML = this.content;

        const footer = document.createElement('div');
        footer.className =
            'flex justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-800';

        if (this.buttons && this.buttons.length > 0) {
            this.buttons.forEach((btnConfig) => {
                const button = document.createElement('button');
                button.type = 'button';
                const baseClass = btnConfig.class || 'border border-slate-200 text-slate-700 hover:border-slate-300 dark:border-slate-700 dark:text-slate-200 dark:hover:border-slate-600';
                button.className = `rounded-md px-4 py-1.5 text-sm font-semibold ${baseClass}`;
                button.textContent = btnConfig.name;

                if (btnConfig.dismiss) {
                    button.addEventListener('click', () => this.hide());
                }
                if (btnConfig.onClick) {
                    button.addEventListener('click', (e) => {
                        btnConfig.onClick.call(button, { data: { modal: this.modal }, event: e });
                    });
                }
                footer.appendChild(button);
            });
        }

        this.modal.appendChild(header);
        this.modal.appendChild(body);
        this.modal.appendChild(footer);
        backdrop.appendChild(this.modal);
        document.body.appendChild(backdrop);

        this.events.filter((e) => e.action === 'show').forEach((event) => {
            event.callback({ data: { modal: this.modal } });
        });

        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop) this.hide();
        });

        return this;
    }

    hide() {
        // Remove this dialog's own backdrop, not whichever happens to match
        // a shared id — otherwise closing a stacked dialog kills the parent.
        if (this.backdrop && this.backdrop.parentNode) {
            this.backdrop.parentNode.removeChild(this.backdrop);
        }
        this.backdrop = null;
        this.modal = null;
        this.events.filter((e) => e.action === 'hide').forEach((event) => {
            event.callback({ data: {} });
        });
    }
}
